import { useState, useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AnalyzeTodoClarifyingQuestion } from '@dreamai/types';
import api from '../api';

interface UseRadiusCheckResult {
  /** Whether a specific field is currently being checked */
  radiusChecking: Record<string, boolean>;
  /** Whether a specific field is within radius (true) or outside (false) */
  radiusResults: Record<string, boolean>;
  /** Check if a field value is within the Dreamster operating radius */
  checkRadiusForField: (fieldKey: string, value: string) => Promise<void>;
  /** Whether any mustBeWithinRadius field in the given questions is outside radius */
  hasFieldOutsideRadius: (
    questions: AnalyzeTodoClarifyingQuestion[],
    answers: Record<string, string>,
  ) => boolean;
  /** Reset all radius state */
  resetRadius: () => void;
}

export function useRadiusCheck(): UseRadiusCheckResult {
  const { getAccessTokenSilently } = useAuth0();
  const [radiusChecking, setRadiusChecking] = useState<Record<string, boolean>>(
    {},
  );
  const [radiusResults, setRadiusResults] = useState<Record<string, boolean>>(
    {},
  );

  const checkRadiusForField = useCallback(
    async (fieldKey: string, value: string) => {
      setRadiusChecking((prev) => ({ ...prev, [fieldKey]: true }));
      try {
        const token = await getAccessTokenSilently();
        const response = await api.post(
          '/user-todos/check-radius',
          { address: value },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const withinRadius = response.data?.withinRadius ?? true;
        setRadiusResults((prev) => ({ ...prev, [fieldKey]: withinRadius }));
      } catch (err) {
        console.error('Error checking radius:', err);
        // On error, don't block — assume within radius
        setRadiusResults((prev) => ({ ...prev, [fieldKey]: true }));
      } finally {
        setRadiusChecking((prev) => ({ ...prev, [fieldKey]: false }));
      }
    },
    [getAccessTokenSilently],
  );

  const hasFieldOutsideRadius = useCallback(
    (
      questions: AnalyzeTodoClarifyingQuestion[],
      answers: Record<string, string>,
    ): boolean => {
      return questions.some(
        (q) =>
          q.mustBeWithinRadius &&
          answers[q.fieldKey] &&
          radiusResults[q.fieldKey] === false,
      );
    },
    [radiusResults],
  );

  const resetRadius = useCallback(() => {
    setRadiusChecking({});
    setRadiusResults({});
  }, []);

  return {
    radiusChecking,
    radiusResults,
    checkRadiusForField,
    hasFieldOutsideRadius,
    resetRadius,
  };
}
